/**
 * MietvertraegeTab — Tab 5 im FahrzeugDetailDrawer
 *
 * Zeigt den mit dem Fahrzeug verknüpften Unfallersatz-Mietvertrag (Cross-Link
 * über `fahrzeuge.mietvertrag_akte_id`). Ohne Link → Leer-State. Mit Link →
 * Kachel mit Vertragslaufzeit, Mieter und Status; Klick öffnet die verknüpfte
 * Akte in neuem Tab.
 *
 * Part of Issue #186 / Parent #183.
 */

import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  buildMietvertragHref,
  formatLaufzeit,
  getMietvertragStatusLabel,
  type MietvertragAkte,
} from '@/lib/mietvertraege-tab';

interface MietvertraegeTabProps {
  fahrzeug: { id: string };
}

const AKTE_COLUMNS = [
  'id',
  'akten_nr',
  'akten_nummer',
  'business_unit',
  'kunde_name',
  'schadendatum',
  'status',
  'mietbeginn',
  'mietende',
].join(', ');

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  neu: { bg: '#E0E7FF', text: '#4338CA' },
  in_bearbeitung: { bg: '#FEF3C7', text: '#92400E' },
  warten: { bg: '#F3F4F6', text: '#6B7280' },
  abgeschlossen: { bg: '#D1FAE5', text: '#047857' },
  storniert: { bg: '#FEE2E2', text: '#991B1B' },
};

export default function MietvertraegeTab({ fahrzeug }: MietvertraegeTabProps) {
  const [loading, setLoading] = useState(true);
  const [akte, setAkte] = useState<MietvertragAkte | null>(null);

  const fetchMietvertrag = useCallback(async () => {
    setLoading(true);
    const { data: fzRow, error: fzError } = await supabase
      .from('fahrzeuge')
      .select('mietvertrag_akte_id')
      .eq('id', fahrzeug.id)
      .maybeSingle();
    if (fzError) {
      console.error('Mietverträge-Tab: fahrzeug lookup failed', fzError);
      toast.error('Fehler beim Laden des Mietvertrags');
      setAkte(null);
      setLoading(false);
      return;
    }
    const akteId = (fzRow as { mietvertrag_akte_id?: string | null } | null)
      ?.mietvertrag_akte_id;
    if (!akteId) {
      setAkte(null);
      setLoading(false);
      return;
    }
    const { data: akteRow, error: akteError } = await supabase
      .from('akten')
      .select(AKTE_COLUMNS)
      .eq('id', akteId)
      .maybeSingle();
    if (akteError) {
      console.error('Mietverträge-Tab: akte lookup failed', akteError);
      toast.error('Fehler beim Laden der verknüpften Akte');
      setAkte(null);
      setLoading(false);
      return;
    }
    setAkte((akteRow as unknown as MietvertragAkte) ?? null);
    setLoading(false);
  }, [fahrzeug.id]);

  useEffect(() => {
    void fetchMietvertrag();
  }, [fetchMietvertrag]);

  if (loading) {
    return (
      <div className="space-y-2" data-testid="mietvertraege-skeleton">
        <div className="h-20 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    );
  }

  if (!akte) {
    return (
      <div
        className="text-center py-12 text-sm text-gray-400"
        data-testid="mietvertraege-empty"
      >
        Kein Mietvertrag mit diesem Fahrzeug verknüpft
      </div>
    );
  }

  const href = buildMietvertragHref(akte);
  const laufzeit = formatLaufzeit(akte.mietbeginn, akte.mietende);
  const statusLabel = getMietvertragStatusLabel(akte.status);
  const statusColor = akte.status ? STATUS_COLORS[akte.status] : null;

  const open = () => {
    if (!href) {
      toast.error('Verknüpfte Akte hat keine gültige Akten-Nummer');
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={!href}
      data-testid="mietvertraege-card"
      className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 hover:shadow-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#F3F4F6' }}
          >
            <FileText className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">
              {akte.kunde_name || 'Mietvertrag'}
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5 truncate">
              {laufzeit}
            </div>
          </div>
        </div>
        <ExternalLink className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
      </div>
      <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-gray-50">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
          style={
            statusColor
              ? { backgroundColor: statusColor.bg, color: statusColor.text }
              : { backgroundColor: '#F3F4F6', color: '#6B7280' }
          }
        >
          {statusLabel}
        </span>
        <span className="text-[10px] font-mono text-gray-400 truncate">
          {akte.akten_nummer || akte.akten_nr || '—'}
        </span>
      </div>
    </button>
  );
}
